import * as React from 'react';
import { PostList } from '../../content/Feed';
import { getAccountName } from '../../content/Index';
import { getPostByChannelId } from '../../globals/ApiHooks';
import { TranslatedTerms } from '../../globals/TranslatedTerms';
import { lang, translateContent } from '../../globals/Translations';
import { IsHorizontalMobile, IsTablet, useWindowSize } from '../../globals/UIHooks';
import { calculateInvestmentProjectPaidBackAmount, GetInvestmentRelizedReturn } from '../../shared/BusinessIntelligence';
import Contract from '../../shared/Contract';
import Post from '../../shared/Post';
import { getProjectStatus, ProjectStatus } from '../../shared/ProjectStatus';
import { InvestInfo } from '../../shared/UI';
import { NiceIntrestRate, NiceNumberWithCurrency } from '../../shared/UIFormatting';
import { ToolTip } from '../atoms/Inputs';
import ProjectCard, { GetDurationString, Row } from '../atoms/ProjectCard';
import { ColumnFlexBox, RowFlexBox } from '../templates/Wrappers';
import colors from '../../globals/colors';


const ExpectedReturnWithToolTop: React.FC<{ avgReturn: string, avgMonths: number }> = ({ avgReturn, avgMonths }) => (
  <RowFlexBox style={{ width: 200, justifyContent: 'space-between' }}>
    <ToolTip content={lang(TranslatedTerms.INTEREST_CALCULATED_BASED_ON_X_MONTHS).split('{X}').join(avgMonths.toString())} />
    {avgReturn}
  </RowFlexBox>
)

const ContractLink: React.FC<{ contract: Contract }> = ({ contract }) => {
  const title = lang(TranslatedTerms.CONTRACT_SIGNED)

  if (!contract.contractURL) return <span>{title}</span>
  const contractBookProvider = contract.contractURL.includes('contractbook')
  if (contractBookProvider) return <a target='_blank' href={contract.contractURL}>{title} (Via Contractbook.com)</a>

  return (
    <a href={contract.contractURL} target='_blank'>{title} (Via Contract-cloud.io)</a>
  )
}

export const InvestmentDetails: React.FC<{ investInfo: InvestInfo }> = ({ investInfo }) => {
  const [posts, setPosts] = React.useState<Post[]>()
  React.useEffect(() => {
    (async () => {
      if (investInfo) {
        const result = await getPostByChannelId(investInfo?.project.projectId)
        if (result) setPosts(result)
      }
    })()
  }, [investInfo])

  const mobile = IsHorizontalMobile(useWindowSize().width)
  const tablet = IsTablet(useWindowSize().width)

  const account = investInfo && investInfo.investor.accounts.find((item) => item.accountId === investInfo.investment.investorAccountId)

  const accountName = investInfo && account && getAccountName(account, investInfo.investor)

  const projectTitle = investInfo?.project.projectTitle
  const amount = investInfo?.investment.amount
  const created = investInfo?.investment.investmentTerms.created
  const interestRate = investInfo?.investment.investmentTerms.interestRate
  const contracts = investInfo?.investment.investmentTerms.contracts

  const avgReturn = investInfo ? NiceNumberWithCurrency(GetInvestmentRelizedReturn(investInfo.investment, investInfo.project).avg) : ''
  const minReturn = investInfo ? NiceNumberWithCurrency(GetInvestmentRelizedReturn(investInfo.investment, investInfo.project).min) : ''
  const maxReturn = investInfo ? NiceNumberWithCurrency(GetInvestmentRelizedReturn(investInfo.investment, investInfo.project).max) : ''
  const actualReturn = investInfo ? NiceNumberWithCurrency(GetInvestmentRelizedReturn(investInfo.investment, investInfo.project).actual) : ''
  const interestStartDate = investInfo && investInfo.project.state.interestStartDate ? (new Date(investInfo.project.state.interestStartDate)).toDateString() : lang(TranslatedTerms.UNKNOWN)

  const interestEndDate = investInfo && investInfo.project.state.interestEndDate ? (new Date(investInfo.project.state.interestEndDate)).toDateString() : lang(TranslatedTerms.UNKNOWN)

  const minMonth = investInfo ? investInfo.project.finance.durationMin : 0
  const maxMonth = investInfo ? investInfo.project.finance.durationMax : 0

  const projectAccount = (investInfo && investInfo.project.nonLocalizedContent?.bankAccount) || ''
  const projectBank = (investInfo && investInfo.project.nonLocalizedContent?.bankAccountName) || ''
  const projectBankLink = (investInfo && investInfo.project.nonLocalizedContent?.bankAccountURL) || ''

  const isProjectPaidBack = !!investInfo && [ProjectStatus.PROJECT_COMPLETED, ProjectStatus.FAILED].includes(getProjectStatus(investInfo.project))
  const isProjectFailed = !!investInfo && [ProjectStatus.FAILED].includes(getProjectStatus(investInfo.project))
  const isProjectInFailProcess = !!investInfo && [ProjectStatus.FAILD_PROCESS].includes(getProjectStatus(investInfo.project))

  const morarente = investInfo?.morarente ? investInfo.morarente : 0
  const morarenteAmount = investInfo.investment.amount * morarente

  const paidBackAmount = calculateInvestmentProjectPaidBackAmount(investInfo.project, investInfo.investment)
  const paidBackLabel =  paidBackAmount !== undefined && NiceNumberWithCurrency(paidBackAmount) || lang(TranslatedTerms.UNKNOWN)

  return (
    <div>
      <h1>{accountName}</h1>
      <h3 style={{ marginBottom: 40 }}>{projectTitle && translateContent(projectTitle)}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? "1fr" : (tablet ? "1fr 1fr" : "2fr 1fr"), gridColumnGap: 20, gridRowGap: 20 }}>
        <ColumnFlexBox>
          <div style={{ display: 'grid', gridTemplateColumns: tablet ? "1fr" : "1fr 1fr", gridColumnGap: 20 }}>
            <ColumnFlexBox>
              <h4>{lang(TranslatedTerms.DETAILS)}</h4>
              {Row(lang(TranslatedTerms.AMOUNT), amount ? NiceNumberWithCurrency(investInfo?.investment.amount) : '', 1)}
              {Row(lang(TranslatedTerms.CREATED), created ? (new Date(created)).toDateString() : '', 2)}
              {Row(lang(TranslatedTerms.INTEREST_RATE_PR_Y), interestRate ? NiceIntrestRate(interestRate, true) : '', 3)}

              <h4 style={{ marginTop: 11 }}>{lang(TranslatedTerms.DEPOSIT_ACCOUNT)}</h4>
              {Row(lang(TranslatedTerms.STATUS), lang(TranslatedTerms.DEPOSIT_RECIVED), 4)}
              {Row(lang(TranslatedTerms.DEPOSIT_ACCOUNT), projectAccount, 5)}
              {Row(lang(TranslatedTerms.BANK), <a href={projectBankLink} target='_blank' >{projectBank}</a>, 6)}
            </ColumnFlexBox>
            <ColumnFlexBox>
              {isProjectPaidBack && !isProjectFailed && (
                <>
                  <h4>{lang(TranslatedTerms.RETURN_PAYMENT)}</h4>
                  {Row(lang(TranslatedTerms.INTREST_REVENUE), actualReturn, 1)}
                  {Row(lang(TranslatedTerms.RETURN_PAYMENT_DATE), interestEndDate, 2)}
                </>)}
              {!isProjectPaidBack && !isProjectInFailProcess && (
                <>
                  <h4>{lang(TranslatedTerms.EXPECTED_RETURN)}</h4>
                  {Row(lang(TranslatedTerms.EXPECTED_RETURN), <ExpectedReturnWithToolTop avgReturn={avgReturn} avgMonths={(minMonth + maxMonth) / 2} />, 1)}
                  {Row(lang(TranslatedTerms.INTEREST_RATE) + ' ' + minMonth + ' ' + lang(TranslatedTerms.MONTHS).toLocaleLowerCase(), minReturn, 2)}
                  {Row(lang(TranslatedTerms.INTEREST_RATE) + ' ' + maxMonth + ' ' + lang(TranslatedTerms.MONTHS).toLocaleLowerCase(), maxReturn, 3)}
                </>)}
              {isProjectInFailProcess && (
                <>
                  <h4>{lang(TranslatedTerms.PROJECT_FAILED_PROCESS)}</h4>
                  <p>{lang(TranslatedTerms.INVESTMENT_LOSS_DESCRIPTION)}</p>
                  {Row(lang(TranslatedTerms.PROJECT_START), interestStartDate, 3)}
                </>
              )}
              {isProjectFailed && (
                <>
                  <h4>{lang(TranslatedTerms.PROJECT_FAILED)}</h4>
                  <p>{lang(TranslatedTerms.INVESTMENT_LOSS_DESCRIPTION)}</p>
                  {Row(lang(TranslatedTerms.PROJECT_START), interestStartDate, 3)}
                  {Row(lang(TranslatedTerms.PAID_BACK), paidBackLabel, 4)}
                  <p style={{color: colors.dark, opacity: 0.75, fontStyle: 'italic', lineHeight: 1.2, fontSize:12}}>Bemærk: Det tilbagebetalte beløb er beregnet ud fra det samlede beløb for hele projektet. Vær derfor opmærksom på det præcise beløb, der er angivet i din bankoverførsel, da vi ikke medtager eventuelle bankgebyrer eller afrundinger i vores beregning.</p>
                </>
              )}
              {!isProjectFailed && !isProjectInFailProcess && <>
                <h4 style={{ marginTop: 11 }}>{lang(TranslatedTerms.DURATION)}</h4>
                {Row(lang(TranslatedTerms.PROJECT_START), interestStartDate, 3)}
                {Row(lang(TranslatedTerms.PROJECT_END), interestEndDate, 4)}
              </>}
              {!isProjectFailed && morarente > 0 && (
                <>
                  <h4 style={{ marginTop: 11 }}>{lang(TranslatedTerms.MORARENTE)}</h4>
                  {Row(lang(TranslatedTerms.AMOUNT), NiceNumberWithCurrency(morarenteAmount), 1)}
                  {Row(lang(TranslatedTerms.INTEREST_RATE), NiceIntrestRate(morarente), 2)}
                </>
              )}
            </ColumnFlexBox>
          </div>
          <ColumnFlexBox style={{ marginTop: 20 }}>
            <h4>{lang(TranslatedTerms.CONTRACT)}</h4>
            {contracts && contracts.map((item, key) =>
              Row(
                lang(TranslatedTerms.CONTRACT) + " " + (key + 1),
                <ContractLink contract={item} />,
                key))
            }

          </ColumnFlexBox>
        </ColumnFlexBox>
        <div>
          <ProjectCard project={investInfo?.project} />
        </div>
      </div>
      <ColumnFlexBox>
        <h2 style={{ marginTop: 40 }}>{lang(TranslatedTerms.PROJECT_NEWS)}</h2>
        <PostList posts={posts || []} />
      </ColumnFlexBox>
    </div >

  );
};

